"use client";

import React from "react";
import { ArrowLeft, CircleCheck, Trees } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SingleFlooringConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-[#4A3728] animate-pulse font-serif">載入地板詳情中 Loading...</p>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h2 className="text-2xl font-serif text-foreground mb-4">找不到產品 / Product Not Found</h2>
        <Link to="/ProductsPage">
          <Button className="bg-[#4A3728] text-white">返回產品藝廊 / Back to Gallery</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  // 優先抓取 WP 原始資料，確保內容完整
  const title = post.title?.rendered || post.title;
  const content = post.content?.rendered || post.content;
  const featuredImage = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618354/img/flooring_product_fallback.png";

  return (
    <div className="container mx-auto px-4 py-12 md:py-24 max-w-6xl">
      {/* 導覽回列表 */}
      <div className="mb-12">
        <Link to="/ProductsPage" className="inline-block">
          <Button variant="ghost" className="text-[#4A3728] hover:bg-[#4A3728]/10 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回藝廊 / Back to Gallery
          </Button>
        </Link>
      </div>

      <div className="grid gap-16 lg:grid-cols-2 items-start">
        {/* 左側：大圖展示 */}
        <div className="rounded-sm overflow-hidden border border-[#4A3728]/10 shadow-2xl">
          <AspectRatio ratio={1}>
            <img
              src={featuredImage}
              alt={title}
              className="object-cover w-full h-full transition-transform duration-1000 hover:scale-105"
            />
          </AspectRatio>
        </div>

        {/* 右側：文字詳情 */}
        <div className="flex flex-col space-y-10">
          <div className="text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl font-serif font-semibold text-[#4A3728] tracking-tight mb-4">
              {title}
            </h1>
            <div className="h-1 bg-[#4A3728] w-20 mx-auto lg:mx-0 opacity-80" />
          </div>

          <Separator className="bg-[#4A3728]/10" />

          {/* 產品內容 */}
          <div className="space-y-6">
            <h3 className="text-xl font-serif font-medium text-[#4A3728] flex items-center gap-2">
              <Trees className="size-5" /> 產品描述 Description
            </h3>
            <div 
              className="prose prose-stone max-w-none text-muted-foreground leading-relaxed font-sans"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>

          {/* 特色亮點 (固定展示) */}
          <div className="bg-[#4A3728]/5 rounded-lg p-8 border border-[#4A3728]/10 shadow-sm">
            <h4 className="font-serif font-bold text-[#4A3728] mb-6 text-lg">產品特色 / Advantages</h4>
            <ul className="space-y-4">
              <li className="flex items-start text-sm text-[#4A3728]/80">
                <CircleCheck className="h-5 w-5 text-[#4A3728] mr-3 shrink-0" />
                <span>天然木質感，觸感溫潤舒適 / Natural wood texture, warm and comfortable touch.</span>
              </li>
              <li className="flex items-start text-sm text-[#4A3728]/80">
                <CircleCheck className="h-5 w-5 text-[#4A3728] mr-3 shrink-0" />
                <span>高耐磨係數，適合各式空間 / High durability, suitable for various interior spaces.</span>
              </li>
              <li className="flex items-start text-sm text-[#4A3728]/80">
                <CircleCheck className="h-5 w-5 text-[#4A3728] mr-3 shrink-0" />
                <span>環保無毒材質，守護家人健康 / Eco-friendly materials, protecting your family's health.</span>
              </li>
            </ul>
          </div>

          {/* 底部行動按鈕 */}
          <div className="pt-6">
            <Link to="/ContactUs" className="w-full">
              <Button size="lg" className="w-full bg-[#4A3728] text-white hover:bg-[#4A3728]/90 py-8 text-lg font-serif tracking-widest shadow-lg">
                預約實體看樣 / Request Consultation
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SingleFlooringTemplateSection() {
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "flooring", p: 1 }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-flooring-template-section" className="bg-background min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SingleFlooringConsumer />
      </PostsProvider>
    </section>
  );
}